import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Button, Heading, Select, Spinner, Text } from "grommet";
import {
  Refresh,
  FingerPrint,
  ContactInfo,
  Unlock,
  Wifi,
  AppsRounded,
  PhoneVertical,
  Sd,
} from "grommet-icons";
import moment from "moment-timezone";
import DoorService from "@services/door.service";
import { presentToastErrorContent } from "@shared/Toast";
import { useFetchDoors, useFetchHotel } from "./redux/hooks";
import { useTranslation } from "react-i18next";

const RECORD_TYPES = {
  MOBILE_KEY: 1,
  FACE: 5,
  PASSCODE: 4,
  KEYCARD: 7,
  FINGERPRINT: 8,
  REMOTE: 55,
  LOXE_PRO_APP: 12,
};

const RECORD_TYPE_LABELS_KEYS = {
  [RECORD_TYPES.MOBILE_KEY]: "unlockRecords.methodMobileKey",
  [RECORD_TYPES.FACE]: "unlockRecords.methodFace",
  [RECORD_TYPES.PASSCODE]: "unlockRecords.methodPasscode",
  [RECORD_TYPES.KEYCARD]: "unlockRecords.methodKeycard",
  [RECORD_TYPES.FINGERPRINT]: "unlockRecords.methodFingerprint",
  [RECORD_TYPES.REMOTE]: "unlockRecords.methodRemote",
  [RECORD_TYPES.LOXE_PRO_APP]: "unlockRecords.methodLoxePro",
};

const getRecordTypeIcon = (recordType, success) => {
  const color = success === 1 ? "status-ok" : "status-critical";

  switch (recordType) {
    case RECORD_TYPES.MOBILE_KEY:
      return { icon: PhoneVertical, color };
    case RECORD_TYPES.FACE:
      return { icon: ContactInfo, color };
    case RECORD_TYPES.PASSCODE:
      return { icon: AppsRounded, color };
    case RECORD_TYPES.KEYCARD:
      return { icon: Sd, color };
    case RECORD_TYPES.FINGERPRINT:
      return { icon: FingerPrint, color };
    case RECORD_TYPES.REMOTE:
      return { icon: Wifi, color };
    case RECORD_TYPES.LOXE_PRO_APP:
      return { icon: PhoneVertical, color };
    default:
      return { icon: Unlock, color };
  }
};

const getDoorLabel = (door) => {
  if (!door) return "";
  return door.type === "room" ? `Room ${door.name}` : door.name;
};

export default function UnlockRecords() {
  const { hotel } = useFetchHotel();
  const { doors } = useFetchDoors();
  const [selectedDoor, setSelectedDoor] = useState(null);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();

  const getRecordTypeLabelT = (recordType) =>
    t(RECORD_TYPE_LABELS_KEYS[recordType] || "unlockRecords.methodUnknown");

  const lockDoors = useMemo(() => {
    if (!doors) return [];
    return doors
      .filter((door) => door.lockId)
      .sort((a, b) => (a.name < b.name ? -1 : 1));
  }, [doors]);

  useEffect(() => {
    if (!selectedDoor && lockDoors.length === 1) {
      setSelectedDoor(lockDoors[0]);
    }
  }, [lockDoors, selectedDoor]);

  const fetchRecords = useCallback(async () => {
    if (!selectedDoor) return;
    setLoading(true);
    try {
      const response = await DoorService.getUnlockRecords(selectedDoor._id);
      setRecords(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      presentToastErrorContent(error);
    } finally {
      setLoading(false);
    }
  }, [selectedDoor]);

  useEffect(() => {
    if (selectedDoor) {
      fetchRecords();
    } else {
      setRecords([]);
    }
  }, [selectedDoor, fetchRecords]);

  const formatDate = (timestamp) =>
    moment(timestamp)
      .tz(hotel.timezone || "America/New_York")
      .format("MMM D, YYYY - h:mm A");

  return (
    <Box
      align="center"
      justify="start"
      pad="medium"
      gap="medium"
      flex="grow"
      margin={{ bottom: "300px" }}
      width="100%"
    >
      <Box
        direction="row"
        justify="between"
        align="center"
        width="100%"
        gap="medium"
      >
        <Box>
          <Heading level="2" margin={{ bottom: "xsmall" }}>
            {t("unlockRecords.title")}
          </Heading>
          <Text size="small" color="dark-6">
            {t("unlockRecords.totalRecords", { count: records.length })}
          </Text>
        </Box>
        <Button
          icon={loading ? <Spinner size="xsmall" /> : <Refresh />}
          label={t("unlockRecords.refresh")}
          onClick={fetchRecords}
          disabled={!selectedDoor || loading}
        />
      </Box>

      <Box width="100%" gap="small" style={{ maxWidth: 600 }}>
        <Text size="small" color="dark-6">
          {t("unlockRecords.selectDoor")}
        </Text>
        <Select
          placeholder={t("unlockRecords.selectDoor")}
          options={lockDoors}
          value={selectedDoor}
          onChange={({ option }) => setSelectedDoor(option || null)}
          labelKey={(option) => getDoorLabel(option)}
          valueKey={{ key: "_id", reduce: false }}
        />
      </Box>

      {!lockDoors.length && (
        <Box
          pad="medium"
          background="light-2"
          round="small"
          style={{ maxWidth: 600 }}
        >
          <Text size="small">{t("unlockRecords.noDoorsFound")}</Text>
        </Box>
      )}

      {selectedDoor && !loading && records.length === 0 && (
        <Box
          pad="large"
          background="light-2"
          round="medium"
          style={{ maxWidth: 600 }}
        >
          <Text weight="bold">{t("unlockRecords.noRecords")}</Text>
          <Text size="small" color="dark-6">
            {t("unlockRecords.noRecordsDesc")}
          </Text>
        </Box>
      )}

      <Box width="100%" gap="small" style={{ maxWidth: 600 }}>
        {records.map((record) => {
          const isSuccess = record.success === 1;
          const typeLabel = getRecordTypeLabelT(record.recordType);
          const username = record.username || t("unlockRecords.methodUnknown");
          const pinLabel = record.keyboardPwd
            ? ` | PIN: ${record.keyboardPwd}`
            : "";
          const iconConfig = getRecordTypeIcon(
            record.recordType,
            record.success,
          );
          const RecordIcon = iconConfig.icon;

          return (
            <Box
              key={record.recordId || `${record.lockDate}-${username}`}
              pad="medium"
              round="small"
              background="white"
              border={{ color: iconConfig.color }}
              gap="xsmall"
            >
              <Box direction="row" justify="between" align="center">
                <Box direction="row" align="center" gap="small">
                  <Box
                    round="full"
                    background={`${iconConfig.color}-10`}
                    pad="xsmall"
                  >
                    <RecordIcon color={iconConfig.color} />
                  </Box>
                  <Text weight="bold">{typeLabel}</Text>
                </Box>
                <Text
                  size="small"
                  color={isSuccess ? "status-ok" : "status-critical"}
                >
                  {isSuccess ? "Success" : "Failed"}
                </Text>
              </Box>
              <Text size="small" color="dark-6">
                {username}
                {pinLabel}
              </Text>
              <Text size="small" color="dark-6">
                {formatDate(record.lockDate)}
              </Text>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
